/**
 * CampaignPoster — auth-aware <img> for /api/campaigns/{id}/poster.
 *
 * The endpoint is tenant-scoped (require_active_user + company scope),
 * so a bare <img src=...> would 401 — the browser doesn't carry the
 * JWT bearer header on image fetches. We fetch via axios (which the
 * site's interceptor authenticates), turn the response into a blob
 * URL, and render that.
 *
 * Props:
 *   campaignId  - integer
 *   hasPoster   - bool; when false we render the placeholder tile
 *                 immediately without firing a request
 *   width, height - tile dimensions in px (default 2:3 poster aspect)
 *   onUpload, onClear - optional callbacks for the operator-edit
 *                 affordance on the Statistics page; when provided,
 *                 hover surfaces Edit / Remove buttons
 *   editable    - shorthand to enable the hover controls
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Upload as UploadIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';


export default function CampaignPoster({
  campaignId,
  hasPoster,
  orientation,   // 'portrait' | 'landscape' | 'square' | null
  shortEdge = 60, // controls tile size; the other edge scales with aspect
  editable = false,
  fillContainer = false,  // v10.19.19: stretch to parent dimensions
  onUpload,
  onClear,
}) {
  // v10.19.16: tile dimensions adapt to the uploaded image's aspect.
  // Portrait → 2:3 (60×90 default). Landscape → 16:9. Square → 1:1.
  // Unknown / no poster → default to portrait so the placeholder
  // matches the most common shape. fillContainer mode skips the
  // calculated size and lets the parent dictate dimensions instead.
  let width, height;
  if (fillContainer) {
    width = '100%';
    height = '100%';
  } else if (orientation === 'landscape') {
    width = Math.round(shortEdge * 16 / 9);
    height = shortEdge;
  } else if (orientation === 'square') {
    width = shortEdge;
    height = shortEdge;
  } else {
    // portrait or unknown
    width = shortEdge;
    height = Math.round(shortEdge * 3 / 2);
  }
  const [blobUrl, setBlobUrl] = useState(null);
  // v10.19.16: bump after upload/clear so the useEffect refetches even
  // when hasPoster + orientation don't change (e.g., replacing one
  // portrait poster with another portrait one).
  const [reloadNonce, setReloadNonce] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let current = null;
    if (!hasPoster || !campaignId) {
      setBlobUrl(null);
      return;
    }
    api.get(`/api/campaigns/${campaignId}/poster`, { responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        current = URL.createObjectURL(r.data);
        setBlobUrl(current);
      })
      .catch(() => { /* 401 / 404 — fall back to placeholder */ });
    return () => {
      cancelled = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [campaignId, hasPoster, orientation, reloadNonce]);

  const triggerPicker = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    e.stopPropagation();
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      await api.post(`/api/campaigns/${campaignId}/poster`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReloadNonce((n) => n + 1);
      onUpload?.();
    } catch (_e) { /* surface elsewhere if needed */ }
    // Reset so re-picking the same file fires onChange again.
    e.target.value = '';
  };

  const handleClear = async (e) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/campaigns/${campaignId}/poster`);
      setBlobUrl(null);
      setReloadNonce((n) => n + 1);
      onClear?.();
    } catch (_e) { /* */ }
  };

  return (
    <Box sx={{
      position: 'relative',
      width, height,
      ...(fillContainer ? {} : { flexShrink: 0, borderRadius: 0.5, border: 1, borderColor: 'divider' }),
      bgcolor: 'background.default',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
      '& .poster-actions': { opacity: 0, transition: 'opacity 0.15s' },
      '&:hover .poster-actions': { opacity: 1 },
    }}>
      {blobUrl ? (
        <Box component="img" src={blobUrl} alt="Campaign poster"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <Box sx={{ color: 'text.disabled', fontSize: width >= 120 ? '0.75rem' : '0.55rem', textAlign: 'center', px: 0.5 }}>
          {hasPoster ? 'Loading…' : 'No poster'}
        </Box>
      )}
      {editable && (
        <Box className="poster-actions" sx={{
          position: 'absolute', inset: 0,
          bgcolor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
        }}>
          <Tooltip title={blobUrl ? 'Replace poster' : 'Upload poster'}>
            <IconButton size="small" onClick={triggerPicker} sx={{ color: '#fff' }}>
              <UploadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {blobUrl && (
            <Tooltip title="Remove poster">
              <IconButton size="small" onClick={handleClear} sx={{ color: '#fff' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
        </Box>
      )}
    </Box>
  );
}
